/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LES RÈGLES PURES QUE LES DEUX CÔTÉS REJOUENT — ET C'EST LE RENVOI DU LOT-2.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Le LOT-2 avait laissé cette ligne au lot qui crée `web/` : « **les règles pures ne
 * montent pas dans `shared/lib/` tant qu'aucun front ne les réapplique.** Le lot qui crée
 * `web/` les y déplace : **la jauge en pourcentage, le seuil de six flux et les plafonds
 * effectifs** sont celles qu'un front rejouera. » Le front existe, et il les rejoue.
 *
 * **POURQUOI ELLES MONTENT, ET CE N'EST PAS POUR ÉCONOMISER DES LIGNES.** `shared/CLAUDE.md`
 * range la logique métier pure ici « pour garantir que le web, le mobile et l'admin
 * appliquent **la même règle** ». Le cas de la jauge le montre en un mot : le serveur rend
 * un pourcentage à chaque écriture, **et le front doit l'afficher entre deux écritures**,
 * pendant qu'on trace. Deux calculs du même nombre, l'un en TypeScript côté API et l'autre
 * recopié dans un composant, **divergeraient au premier arrondi** — et personne ne le
 * verrait, puisque les deux auraient l'air justes.
 *
 * **CE QUI RESTE À `api/src/courses/regles.ts`, ET POURQUOI.** Tout ce qu'aucun front ne
 * rejoue : les huit motifs de fin, les délais des passages automatiques, la borne de perte
 * de contenu. Monter une règle que le front ne connaît pas ne fait que l'éloigner de son
 * seul lecteur.
 *
 * **CE FICHIER NE FAIT AUCUN EFFET DE BORD, ET IL N'EN FERA JAMAIS.** `shared/` « ne
 * contient aucune interface utilisateur et n'importe jamais de code applicatif ». Pas
 * d'accès base, pas d'horloge, pas de réseau : ce sont des fonctions qui prennent des
 * nombres et rendent des nombres, donc des fonctions qu'un test exerce sans rien monter.
 */
// ---------------------------------------------------------------------------
// U-05 — le plafond d'élèves effectif
// ---------------------------------------------------------------------------
/**
 * **Le plafond d'élèves effectif est le plus BAS des deux** (U-05, Q551).
 *
 * Le réglage de structure et celui du parc gouvernent la même chose, et le parc est là
 * pour **borner** ce qu'une structure se donne : prendre le plus haut rendrait le réglage
 * du parc décoratif, et prendre celui de la structure seule le rendrait inexistant.
 *
 * **Le front la rejoue** sur l'écran de création d'un cours, pour dire combien d'élèves
 * peuvent encore entrer avant que le serveur refuse.
 */
export function plafondElevesEffectif(reglageStructure, reglageParc) {
    return Math.min(reglageStructure, reglageParc);
}
// ---------------------------------------------------------------------------
// F-21 — la jauge du tableau, et ses deux unités
// ---------------------------------------------------------------------------
/**
 * **La jauge affiche un pourcentage, et elle NE NOMME PAS la structure dont vient son
 * plafond** — repli de la zone floue 9.
 *
 * « Sur un cours mixte, le plafond est celui de la structure du tuteur, et **un élève
 * d'une autre structure lirait sinon un réglage qui n'est pas le sien**. C'est le patron
 * du bandeau d'observation, qui signale qu'une structure regarde **sans la nommer**. »
 * Cette fonction ne prend donc aucun nom de structure, et il n'y a nulle part où en
 * passer un.
 *
 * **Le front la rejoue à chaque trait**, entre deux réponses du serveur : « une jauge
 * permanente est visible de tous **pendant tout le cours** », et attendre la réponse
 * suivante la ferait sauter par à-coups.
 */
export function pourcentageDeRemplissage(poidsCourant, plafondOctets) {
    if (plafondOctets <= 0) {
        return 100;
    }
    return Math.min(100, Math.round((poidsCourant / plafondOctets) * 100));
}
/**
 * **Un dépôt qui atteindrait le plafond est refusé EN ENTIER** (F-24 critère 2, Q367,
 * Q368) : rien n'est posé, et le dépôt ne déborde jamais sur un tableau neuf.
 *
 * **Le front la rejoue pour dire NON AVANT d'envoyer** : téléverser trois mégaoctets pour
 * apprendre au retour qu'ils ne rentraient pas fait payer l'attente à la personne, sur un
 * refus que le produit connaissait d'avance. **Le serveur refuse quand même** — c'est lui
 * qui fait autorité, et une garde de front ne garde rien.
 */
export function verdictDeDepot(poidsCourant, besoin, plafondOctets) {
    if (poidsCourant + besoin <= plafondOctets) {
        return { accepte: true };
    }
    return {
        accepte: false,
        placeRestante: Math.max(0, plafondOctets - poidsCourant),
        besoin,
    };
}
// ---------------------------------------------------------------------------
// U-15 — le régime dégradé de la visio
// ---------------------------------------------------------------------------
/**
 * **Six flux d'image, et pas un autre nombre** (DD-14).
 *
 * C'est cinq — le plafond d'élèves par défaut — **plus le tuteur** : un cours réglé par
 * défaut ne franchit jamais ce seuil. C'est un **nombre du produit**, pas un réglage de
 * structure, et la zone floue 5 en porte le repli.
 */
export const SEUIL_FLUX_IMAGE = 6;
/**
 * **Le régime dégradé : au-delà de six flux d'image, seuls ceux qui parlent restent en
 * image** (U-15, Q417, DD-15).
 *
 * **Un écran partagé n'est JAMAIS celui qu'on retire** (DD-17) : le produit retire d'abord
 * les visages — « on partage un écran pour qu'il soit vu ». C'est la règle qui fait que
 * cette fonction ne peut pas se réduire à un filtre sur `parle`.
 *
 * Les personnes dont l'image n'est plus diffusée **restent présentes et audibles**, et le
 * produit le leur dit. La bascule est automatique et réversible.
 *
 * **Le front la rejoue parce que c'est lui qui décide quoi peindre** : la liste des flux
 * change à chaque prise de parole, et demander au serveur à chaque fois ferait clignoter
 * les vignettes au rythme du réseau.
 */
export function fluxDiffuses(publies) {
    if (publies.length <= SEUIL_FLUX_IMAGE) {
        return publies.map((flux) => flux.personneId);
    }
    return publies
        .filter((flux) => flux.estUnEcranPartage || flux.parle)
        .map((flux) => flux.personneId);
}
//# sourceMappingURL=regles-partagees.js.map